import React from 'react';
import { Printer, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';

const RESTAURANT_NAME = 'HIBISUN';
const RESTAURANT_ADDRESS = 'Afro-Caribbean Restaurant';

const baseStyle = `
  body { font-family: 'Courier New', monospace; padding: 8px; margin: 0; width: 280px; color: #000; }
  .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
  .header h1 { margin: 0; font-size: 22px; letter-spacing: 2px; }
  .header p { margin: 2px 0; font-size: 12px; }
  .table-num { font-size: 28px; font-weight: bold; margin: 4px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 3px 0; font-size: 14px; }
  .item-name { font-weight: bold; }
  .item-price { text-align: right; }
  .separator { border-top: 1px dashed #000; margin: 6px 0; }
  .total-line { font-size: 18px; font-weight: bold; }
  .notes { border: 2px solid #000; padding: 6px 8px; margin: 8px 0; font-size: 13px; }
  .footer { border-top: 2px dashed #000; padding-top: 6px; margin-top: 8px; text-align: center; font-size: 10px; color: #555; }
  @media print { body { width: 72mm; padding: 2mm; } @page { margin: 0; } }
`;

function openPrintWindow(html) {
  const printWindow = window.open('', '_blank', 'width=350,height=700');
  if (!printWindow) {
    alert('Veuillez autoriser les popups pour imprimer');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR');
}

// ─── Kitchen Ticket ───
export function printKitchenTicket({ tableId, items, notes, orderId, createdAt, serverName, section }) {
  const time = formatTime(createdAt);
  const date = formatDate(createdAt);
  const sectionLabel = section || 'CUISINE';

  const itemsHtml = items.map(item =>
    `<tr><td class="item-name">${item.quantity}x ${item.name}</td></tr>`
  ).join('');

  const notesHtml = notes
    ? `<div class="notes"><strong>!! NOTES !!</strong><br/>${notes}</div>` : '';

  const html = `<!DOCTYPE html><html><head><title>${sectionLabel}</title><style>${baseStyle}
    .header h1 { font-size: 18px; }
    .item-name { font-size: 18px; padding: 5px 0; }
  </style></head><body>
    <div class="header">
      <h1>== ${sectionLabel} ==</h1>
      <p class="table-num">TABLE ${tableId}</p>
      <p>${date} ${time}${serverName ? ` | ${serverName}` : ''}</p>
    </div>
    <table>${itemsHtml}</table>
    ${notesHtml}
    <div class="footer">#${(orderId || '').slice(0, 8)}</div>
    <div id="pf" style="text-align:center;padding:10px;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #ccc;z-index:999"><button onclick="window.print()" style="padding:12px 40px;font-size:16px;font-weight:bold;background:#0A422D;color:#fff;border:none;border-radius:8px;cursor:pointer">IMPRIMER</button></div><script>setTimeout(function(){try{window.print()}catch(e){}},600)<\/script>
  </body></html>`;

  openPrintWindow(html);
}

// ─── Client Receipt ───
export function printClientReceipt({ tableId, items, total, discount, discountAmount, paidAmount, paymentMethod, tip, orderId, createdAt, serverName, isTakeaway }) {
  const time = formatTime(createdAt);
  const date = formatDate(createdAt);
  const afterDiscount = total - (discountAmount || 0);

  const itemsHtml = items.map(item =>
    `<tr><td class="item-name">${item.quantity}x ${item.name}</td><td class="item-price">${(item.price * item.quantity).toLocaleString()} B</td></tr>`
  ).join('');

  const discountHtml = discountAmount > 0
    ? `<tr><td>Remise</td><td class="item-price" style="color:red">-${discountAmount.toLocaleString()} B</td></tr>` : '';

  const tipHtml = tip > 0
    ? `<tr><td>Pourboire</td><td class="item-price">${tip.toLocaleString()} B</td></tr>` : '';

  const methodLabel = paymentMethod === 'cash' ? 'ESPECES' : paymentMethod === 'card_terminal' ? 'CB TERMINAL' : paymentMethod === 'stripe' ? 'CARTE EN LIGNE' : paymentMethod === 'split' ? 'SPLIT' : (paymentMethod || '').toUpperCase();

  const html = `<!DOCTYPE html><html><head><title>Ticket</title><style>${baseStyle}</style></head><body>
    <div class="header">
      <h1>${RESTAURANT_NAME}</h1>
      <p>${RESTAURANT_ADDRESS}</p>
      <p class="table-num">${isTakeaway ? 'A EMPORTER' : `TABLE ${tableId}`}</p>
      <p>${date} ${time}</p>
    </div>
    <table>
      ${itemsHtml}
      <tr><td colspan="2"><div class="separator"></div></td></tr>
      ${discountHtml}
      <tr class="total-line"><td>TOTAL</td><td class="item-price">${afterDiscount.toLocaleString()} B</td></tr>
      ${tipHtml}
    </table>
    <div class="separator"></div>
    <p style="text-align:center;font-size:13px;font-weight:bold">${methodLabel}</p>
    <div class="footer">
      <p>${serverName ? `Serveur: ${serverName}` : ''}</p>
      <p>#${(orderId || '').slice(0, 8)}</p>
      <p style="margin-top:6px">Merci de votre visite !</p>
      <p>${RESTAURANT_NAME}</p>
    </div>
    <div id="pf" style="text-align:center;padding:10px;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #ccc;z-index:999"><button onclick="window.print()" style="padding:12px 40px;font-size:16px;font-weight:bold;background:#0A422D;color:#fff;border:none;border-radius:8px;cursor:pointer">IMPRIMER</button></div><script>setTimeout(function(){try{window.print()}catch(e){}},600)<\/script>
  </body></html>`;

  openPrintWindow(html);
}

// ─── Split Receipt (partial payment) ───
export function printSplitReceipt({ tableId, selectedItems, splitTotal, paymentMethod, tip, orderId, createdAt, serverName }) {
  const time = formatTime(createdAt);
  const date = formatDate(createdAt);

  const itemsHtml = selectedItems.map(item =>
    `<tr><td class="item-name">${item.qty}x ${item.name}</td><td class="item-price">${(item.price * item.qty).toLocaleString()} B</td></tr>`
  ).join('');

  const methodLabel = paymentMethod === 'cash' ? 'ESPECES' : 'CB TERMINAL';

  const html = `<!DOCTYPE html><html><head><title>Split</title><style>${baseStyle}</style></head><body>
    <div class="header">
      <h1>${RESTAURANT_NAME}</h1>
      <p>ADDITION PARTIELLE</p>
      <p class="table-num">TABLE ${tableId}</p>
      <p>${date} ${time}</p>
    </div>
    <table>
      ${itemsHtml}
      <tr><td colspan="2"><div class="separator"></div></td></tr>
      <tr class="total-line"><td>TOTAL</td><td class="item-price">${splitTotal.toLocaleString()} B</td></tr>
      ${tip > 0 ? `<tr><td>Pourboire</td><td class="item-price">${tip.toLocaleString()} B</td></tr>` : ''}
    </table>
    <div class="separator"></div>
    <p style="text-align:center;font-size:13px;font-weight:bold">${methodLabel}</p>
    <div class="footer">
      <p>${serverName ? `Serveur: ${serverName}` : ''}</p>
      <p>#${(orderId || '').slice(0, 8)}</p>
      <p style="margin-top:6px">Merci !</p>
    </div>
    <div id="pf" style="text-align:center;padding:10px;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #ccc;z-index:999"><button onclick="window.print()" style="padding:12px 40px;font-size:16px;font-weight:bold;background:#0A422D;color:#fff;border:none;border-radius:8px;cursor:pointer">IMPRIMER</button></div><script>setTimeout(function(){try{window.print()}catch(e){}},600)<\/script>
  </body></html>`;

  openPrintWindow(html);
}

// ─── Button Components ───
export const PrintKitchenButton = ({ order, serverName, className = '' }) => (
  <Button onClick={() => printKitchenTicket({
    tableId: order.table_id, items: order.items, notes: order.notes,
    orderId: order.id, createdAt: order.created_at, serverName,
  })} variant="outline" size="sm"
    className={`rounded-xl border-[#D4A853] text-[#D4A853] hover:bg-[#D4A853] hover:text-white ${className}`}
    data-testid={`print-kitchen-${order.id}`}>
    <Printer className="w-4 h-4 mr-1" /> Cuisine
  </Button>
);

export const PrintReceiptButton = ({ order, serverName, className = '' }) => (
  <Button onClick={() => printClientReceipt({
    tableId: order.table_id, items: order.items, total: order.total,
    discount: order.discount_type, discountAmount: order.discount_amount,
    paidAmount: order.paid_amount, paymentMethod: order.payment_method,
    tip: order.tip_amount || 0, orderId: order.id, createdAt: order.created_at,
    serverName, isTakeaway: order.is_takeaway,
  })} variant="outline" size="sm"
    className={`rounded-xl border-[#0A422D] text-[#0A422D] hover:bg-[#0A422D] hover:text-white ${className}`}
    data-testid={`print-receipt-${order.id}`}>
    <Receipt className="w-4 h-4 mr-1" /> Ticket
  </Button>
);
